// Etapa 3 (ADENDA-DIEGO 12-oct, puntos 2 y 3): la generación por kW de ESTA obra. La base es la de Diego (1,750 kWh por kWp al
// año, techo de 15 a 20°, editable en Ajustes); el vendedor la ajusta con factores sugeridos (sol del municipio, inclinación y
// orientación, montaje, sombras) o la escribe, y dice por qué (queda en el historial y en la bitácora de Diego). Nunca sube
// de la base sin elegirlo a sabiendas, y solo en comercio e industria (bifacial sobre suelo o carport). Todo lo prometido
// (paneles, ahorro, cobertura, retorno y el PDF) lo recalcula el servidor con esta cifra: la pantalla solo sugiere.
// Verificación adversarial de la etapa 3: sin cifra escrita, la generación SALE de los factores (el servidor la recalcula con
// la base de Diego y el municipio de hoy); el suelo y el carport solo se ofrecen si el cuestionario dice esa estructura; el
// municipio y "es Sonora" son los del servidor (la misma regla de compartido).
import { useMemo, useState } from 'react';
import { SunMedium } from 'lucide-react';
import {
  FACTORES_BASE,
  GENERACION_BASE_POR_OMISION,
  GENERACION_MAX_KWH_KWP,
  GENERACION_MIN_KWH_KWP,
  MUNICIPIOS_SONORA,
  OPCIONES_MONTAJE,
  OPCIONES_ORIENTACION,
  OPCIONES_SOMBRAS,
  TEXTO_NO_INSTALAR_SOMBRA,
  esSonora,
  estructuraDe,
  factorZona,
  montajeNoCuenta,
  municipioDeCiudad,
  municipioPorClave,
  numero,
  sugerenciaGeneracion,
  textoFactores,
  topeGeneracion,
} from '@cotizador/compartido';
import type { Cuestionario, FactoresGeneracion, GeneracionCotizacion, OpcionFactorGeneracion, ParametrosCalculo, ResultadoMotor } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { Boton, CampoSelect, CampoTexto, Chip, ErrorEnLinea, Interruptor } from '../../componentes/ui.tsx';
import { reemplazo } from '../../logica/ediciones.ts';
import { numeroDe } from '../../logica/cuestionario.ts';
import { usarCotizacion, usarRegenerarPdf } from './contexto.tsx';

const fx = (f: number) => `× ${f.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')}`;
/** Las opciones de un factor con su número; las que no cuentan en esta obra no se ofrecen (salvo la que ya estaba elegida):
 *  suelo y carport solo en comercio e industria y con esa estructura en el cuestionario. */
const opcionesDe = (xs: readonly OpcionFactorGeneracion[], actual: string, vale: (x: OpcionFactorGeneracion) => boolean = () => true) =>
  xs.filter((x) => vale(x) || x.id === actual).map((x) => ({ valor: x.id, texto: x.factor === 1 ? x.texto : `${x.texto} (${fx(x.factor)})` }));

/** Lo que dice la generación en una línea: la cifra, de dónde sale y el municipio. */
function Resumen({ r, base, municipioTexto }: { r: ResultadoMotor | null; base: number; municipioTexto: string | null }) {
  const g = r?.generacion;
  const kwh = g?.kwh_kwp ?? base;
  return (
    <div className="pila pila--chica">
      <p className="generacion__cifra">
        <strong>{numero(kwh)}</strong> kWh por kWp al año
        {!g?.de_vendedor ? (
          <Chip>base de Diego</Chip>
        ) : g.sube_sobre_base ? (
          <Chip tono="ambar">más que la base, a sabiendas</Chip>
        ) : g.zona_sola && !g.a_mano && g.municipio && kwh === g.municipio.sugerido_kwh_kwp ? (
          // ADENDA z46-8 (P20): el sol del municipio bajó la generación solo
          <Chip tono="prusia">sol de {g.municipio.nombre}</Chip>
        ) : (
          <Chip tono="prusia">ajustada para esta obra</Chip>
        )}
      </p>
      {g?.de_vendedor && <p className="nota-suave">La base de Diego es {numero(g.base_kwh_kwp)}{g.motivo ? `. Por qué: ${g.motivo}` : ''}.</p>}
      {g?.a_mano && g.factores_kwh_kwp !== undefined && g.factores_kwh_kwp !== g.kwh_kwp && (
        <p className="nota-suave">Escrita a mano: con los factores elegidos serían {numero(g.factores_kwh_kwp)}.</p>
      )}
      {municipioTexto && <p className="nota-suave">{municipioTexto}</p>}
    </div>
  );
}

export function GeneracionObra({ cuestionario, parametros, r, puedeEditar }: { cuestionario: Cuestionario; parametros: ParametrosCalculo; r: ResultadoMotor | null; puedeEditar: boolean }) {
  const { ejecutar } = usarCotizacion();
  const regenerar = usarRegenerarPdf();
  const negocio = cuestionario.tipo === 'comercio' || cuestionario.tipo === 'industria';
  const estructura = estructuraDe(cuestionario);
  const base = r?.generacion?.base_kwh_kwp ?? GENERACION_BASE_POR_OMISION;
  const guardada = parametros.generacion ?? null;
  // la misma regla que el servidor ("Son.", "Sonora." y "Sonora, México" también son Sonora)
  const sonora = esSonora(cuestionario.cliente.estado);
  // el municipio de la ciudad: el que decidió el servidor; sin cálculo todavía, la misma función de compartido
  const muServidor = r?.generacion ? r.generacion.municipio : undefined;
  const deCiudad = muServidor !== undefined ? (muServidor?.como === 'ciudad' ? municipioPorClave(muServidor.clave) : null) : sonora ? municipioDeCiudad(cuestionario.cliente.ciudad) : null;

  const [abierto, setAbierto] = useState(false);
  const [municipio, setMunicipio] = useState<string>(guardada?.municipio ?? '');
  const [factores, setFactores] = useState<FactoresGeneracion>(guardada?.factores ?? { ...FACTORES_BASE });
  const [valor, setValor] = useState<string>(guardada?.kwh_kwp ? String(guardada.kwh_kwp) : '');
  const [motivo, setMotivo] = useState<string>(guardada?.motivo ?? '');
  const [aSabiendas, setASabiendas] = useState<boolean>(guardada?.sube_a_sabiendas ?? false);
  const [mal, setMal] = useState<{ valor?: string; motivo?: string; municipio?: string }>({});
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const m = deCiudad ?? municipioPorClave(municipio || null);
  // ADENDA z46-8 (P20, Diego 27-oct: «la generación BAJA SOLA con el sol de cada municipio»): con el parámetro de la cotización y un
  // municipio con menos sol que Hermosillo, el sol va solo (como lo calcula el servidor): el interruptor queda prendido y no se apaga.
  const zonaSola = parametros.sol_del_municipio === true && !!m && m.alerta;
  const factoresEf = useMemo(() => (zonaSola ? { ...factores, zona: true } : factores), [zonaSola, factores]);
  const sug = useMemo(() => sugerenciaGeneracion({ base, factores: factoresEf, municipio: m, negocio, estructura }), [base, factoresEf, m, negocio, estructura]);
  const tope = useMemo(() => topeGeneracion({ base, factores: factoresEf, municipio: m, negocio, estructura }), [base, factoresEf, m, negocio, estructura]);
  const aMano = valor.trim() !== '';
  const numValor = aMano ? numeroDe(valor) : sug.kwh_kwp;
  const sube = numValor !== null && numValor > base;

  const g = r?.generacion;
  // el municipio que dice el cálculo (o, con un cálculo de antes, el de la ciudad)
  const mu = g?.municipio ?? (deCiudad ? { nombre: deCiudad.municipio, como: 'ciudad' as const, factor_conservador: deCiudad.factor_conservador, alerta: deCiudad.alerta, sugerido_kwh_kwp: deCiudad.alerta ? Math.round((base * factorZona(deCiudad)) / 10) * 10 : base } : null);
  const municipioTexto = mu
    ? `Obra en ${mu.nombre}${mu.como === 'elegido' ? ' (elegido por el vendedor)' : ''}: ${
        mu.alerta
          ? `tiene ${numero((1 - mu.factor_conservador) * 100, 1)} % menos sol que Hermosillo; ${g?.zona_sola ? `la generación baja sola a ${numero(mu.sugerido_kwh_kwp)} kWh por kWp al año (Diego, 28-oct).` : `se sugieren ${numero(mu.sugerido_kwh_kwp)} kWh por kWp al año.`}`
          : mu.factor_conservador < 1
            ? `casi el mismo sol que Hermosillo (${fx(mu.factor_conservador)}).`
            : mu.nombre === 'Hermosillo'
              ? 'el sol con que se fijó la base.'
              : `tanto sol como Hermosillo o un poco más (${fx(mu.factor_conservador)}; no sube la generación).`
      }`
    : sonora
      ? `No reconozco «${cuestionario.cliente.ciudad}» como municipio de Sonora: elige el municipio de la obra en «Ajustar».`
      : 'Obra fuera de Sonora: el cotizador no tiene el sol de la zona (consulta PVGIS antes de prometer generación).';

  const elegir = (que: keyof FactoresGeneracion, v: string | boolean) => {
    setFactores((f) => ({ ...f, [que]: v }));
    setValor('');
  };

  async function guardar(regresarABase = false) {
    const problemas: typeof mal = {};
    let g2: GeneracionCotizacion;
    if (regresarABase) g2 = { municipio: deCiudad ? null : municipio || null, factores: { ...FACTORES_BASE }, kwh_kwp: null, motivo: null, sube_a_sabiendas: false };
    else {
      if (numValor === null || !Number.isInteger(numValor) || numValor < GENERACION_MIN_KWH_KWP || numValor > GENERACION_MAX_KWH_KWP)
        problemas.valor = `Un número entero entre ${numero(GENERACION_MIN_KWH_KWP)} y ${numero(GENERACION_MAX_KWH_KWP)}.`;
      else if (sube && !negocio) problemas.valor = `En una casa la generación no sube de la base de Diego (${numero(base)}).`;
      else if (numValor > tope)
        problemas.valor =
          tope === base
            ? `Es más que la base de Diego (${numero(base)}) y lo que elegiste no lo justifica: solo sube por la ganancia bifacial sobre suelo o carport (con esa estructura en el cuestionario) que quede después de la inclinación, las sombras y el sol.`
            : `Lo más que justifican los factores elegidos es ${numero(tope)}.`;
      else if (sube && !aSabiendas) problemas.valor = `Es más que la base de Diego (${numero(base)}): marca «Prometo más que la base de Diego, a sabiendas» o bájala.`;
      // Escrita a mano solo si es distinta de la sugerida; si no, la sacan los factores (y sigue a la base y a la obra)
      const kwh = aMano && numValor !== null && numValor !== sug.kwh_kwp ? numValor : null;
      const porQue = motivo.trim() || (kwh !== null && sug.partes.length ? textoFactores(sug.partes) : '');
      if (kwh !== null && porQue.length < 3) problemas.motivo = 'Escribe por qué (lo que viste en la obra).';
      if (porQue.length > 300) problemas.motivo = 'Máximo 300 letras.';
      if (!deCiudad && sonora && !municipio) problemas.municipio = 'Elige el municipio de la obra.';
      setMal(problemas);
      if (Object.keys(problemas).length) return;
      g2 = {
        municipio: deCiudad ? null : municipio || null,
        factores: factoresEf,
        kwh_kwp: kwh,
        motivo: porQue.length >= 3 ? porQue.slice(0, 300) : null,
        sube_a_sabiendas: (kwh ?? sug.kwh_kwp) > base && aSabiendas,
        // lo que daban los factores al escribirla: si después cambia la obra o la base, el servidor avisa
        sugerida_kwh_kwp: kwh !== null ? sug.kwh_kwp : null,
      };
    }
    setOcupado(true);
    setError(null);
    setAviso(null);
    const texto = regresarABase ? 'Generación: regresa a la base de Diego' : `Generación de la obra: ${numero(g2.kwh_kwp ?? sug.kwh_kwp)} kWh por kWp al año${g2.kwh_kwp === null ? (sug.partes.length ? ' (la de los factores)' : ' (la base)') : ' (escrita a mano)'}`;
    try {
      await ejecutar((c) => api.editarBorrador(c.id, c.revision, [reemplazo('/parametros/generacion', c.borrador.parametros.generacion ?? null, g2, g2.motivo ?? texto)]));
      setAbierto(false);
      const x = await regenerar(texto);
      setAviso(x.tipo === 'regenerado' ? `Listo: se generó la versión ${x.numero} del PDF con esta generación.` : x.tipo === 'pendiente' ? `El PDF anterior ya no dice lo último y no se regeneró solo: ${x.motivo}` : null);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  const sombra = OPCIONES_SOMBRAS.find((x) => x.id === factores.sombras);
  return (
    <section className="hoja generacion" aria-labelledby="generacion-titulo" id="generacion">
      <div className="generacion__cabeza">
        <h3 id="generacion-titulo" className="generacion__titulo">
          <SunMedium aria-hidden className="generacion__icono" />
          Generación por kW de esta obra
        </h3>
        {puedeEditar && !abierto && (
          <Boton chico variante="fantasma" onClick={() => setAbierto(true)}>
            Ajustar
          </Boton>
        )}
      </div>
      <Resumen r={r} base={base} municipioTexto={municipioTexto} />
      {aviso && <p className="nota-suave">{aviso}</p>}

      {abierto && (
        <div className="pila generacion__editor">
          <p className="nota-suave">
            Todo lo que se promete (paneles, ahorro, cobertura, retorno y el PDF) usa esta cifra. La base de Diego es {numero(base)} (techo de 15 a 20° al sur). Elige lo que viste en la obra y
            el cotizador sugiere; tú decides, y queda en el historial quién la cambió y por qué.
          </p>
          {!deCiudad && sonora && (
            <CampoSelect
              etiqueta="Municipio de la obra"
              value={municipio}
              disabled={ocupado}
              error={mal.municipio}
              ayuda={`La ciudad del cuestionario («${cuestionario.cliente.ciudad}») no es un municipio que el cotizador reconozca.`}
              onChange={(e) => {
                setMunicipio(e.target.value);
                setValor('');
              }}
              opciones={[{ valor: '', texto: 'Elige el municipio…' }, ...MUNICIPIOS_SONORA.map((x) => ({ valor: x.clave, texto: x.nombre }))]}
            />
          )}
          {m && factorZona(m) < 1 && (
            <Interruptor
              etiqueta={`Ajustar por el sol de ${m.municipio} (${fx(factorZona(m))})`}
              detalle={
                zonaSola
                  ? `Se aplica solo: tiene ${numero((1 - m.factor_conservador) * 100, 1)} % menos sol que Hermosillo (PVGIS) y la cotización nunca promete el sol de Hermosillo (Diego, 28-oct).`
                  : m.alerta
                    ? `Tiene ${numero((1 - m.factor_conservador) * 100, 1)} % menos sol que Hermosillo (PVGIS).`
                    : 'Casi el mismo sol que Hermosillo: no hace falta, pero puedes aplicarlo.'
              }
              marcado={factoresEf.zona}
              disabled={ocupado || zonaSola}
              alCambiar={(v) => elegir('zona', v)}
            />
          )}
          <div className="rejilla-3">
            <CampoSelect etiqueta="Inclinación y orientación" value={factores.orientacion} disabled={ocupado} onChange={(e) => elegir('orientacion', e.target.value)} opciones={opcionesDe(OPCIONES_ORIENTACION, factores.orientacion)} />
            <CampoSelect
              etiqueta="Montaje"
              value={factores.montaje}
              disabled={ocupado}
              ayuda={
                !negocio
                  ? 'Suelo y carport son solo para comercio e industria.'
                  : estructura === 'suelo' || estructura === 'carport'
                    ? `${estructura === 'suelo' ? 'Sobre suelo' : 'En carport'} los bifaciales ganan un poco (estimación por confirmar).`
                    : 'Suelo y carport solo cuentan si el cuestionario dice esa estructura (esta obra es de techo).'
              }
              onChange={(e) => elegir('montaje', e.target.value)}
              opciones={opcionesDe(OPCIONES_MONTAJE, factores.montaje, (x) => montajeNoCuenta(x, negocio, estructura) === null)}
            />
            <CampoSelect etiqueta="Sombras" value={factores.sombras} disabled={ocupado} ayuda={sombra?.visita_tecnica ? 'Pide visita técnica.' : TEXTO_NO_INSTALAR_SOMBRA} onChange={(e) => elegir('sombras', e.target.value)} opciones={opcionesDe(OPCIONES_SOMBRAS, factores.sombras)} />
          </div>
          <p className="generacion__sugerida">
            Sugerida: <strong>{numero(sug.kwh_kwp)}</strong> kWh por kWp al año ({numero(base)} {sug.partes.length ? sug.partes.map((p) => fx(p.factor)).join(' ') : 'sin factores'})
            {sug.ignorados.length ? `. No cuenta: ${sug.ignorados.join('; ')}.` : ''}
          </p>
          <div className="rejilla-2">
            <CampoTexto
              etiqueta="kWh por kWp al año de esta obra"
              inputMode="numeric"
              placeholder={String(sug.kwh_kwp)}
              value={valor}
              disabled={ocupado}
              error={mal.valor}
              ayuda={aMano && numValor !== null && numValor > sug.kwh_kwp ? `Es más de lo que dan tus factores (${numero(sug.kwh_kwp)}): queda con aviso amarillo.` : 'Vacío = la sugerida (sigue a la base de Diego y a la obra).'}
              onChange={(e) => setValor(e.target.value)}
            />
            <CampoTexto
              etiqueta="Por qué"
              value={motivo}
              maxLength={300}
              placeholder={sug.partes.length ? textoFactores(sug.partes) : 'Lo que viste en la obra'}
              disabled={ocupado}
              error={mal.motivo}
              ayuda="Queda en el historial y en la bitácora de Diego."
              onChange={(e) => setMotivo(e.target.value)}
            />
          </div>
          {sube && negocio && (
            <Interruptor
              etiqueta="Prometo más que la base de Diego, a sabiendas"
              detalle="La ganancia bifacial sobre suelo o carport es una estimación por confirmar con una obra medida. La cotización queda con aviso amarillo."
              marcado={aSabiendas}
              disabled={ocupado}
              alCambiar={setASabiendas}
            />
          )}
          <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
          <div className="fila-botones">
            <Boton chico variante="noche" cargando={ocupado} onClick={() => void guardar()}>
              Guardar y recalcular
            </Boton>
            {g?.de_vendedor && !(g.zona_sola && g.municipio && g.kwh_kwp === g.municipio.sugerido_kwh_kwp) && (
              <Boton chico variante="fantasma" disabled={ocupado} onClick={() => void guardar(true)}>
                {g.zona_sola && g.municipio ? `Regresar a la del sol de ${g.municipio.nombre}` : 'Regresar a la base'}
              </Boton>
            )}
            <Boton chico variante="fantasma" disabled={ocupado} onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
          </div>
        </div>
      )}
    </section>
  );
}
